/**
 * SettingsModule -- Full settings page for AWENI.
 *
 * Sections: Profile, Appearance, Modules, Prayer, Notifications,
 * Offline & Storage, Battery & Performance, Permissions, About.
 *
 * All settings persisted to localStorage AND synced to server preferences API.
 * Fully i18n, RTL, and dark-mode aware.
 */

import type { AweniContext, AweniModule, Lang, ThemeMode } from '../../types';

// ---------- Constants ----------

const LS_PREFIX = 'aweni_settings_';

const MODULE_IDS = [
  'games', 'memoriz', 'balancetab', 'selecto', 'blog', 'chat', 'community',
] as const;

const MODULE_ICONS: Record<string, string> = {
  games: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/></svg>',
  memoriz: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/></svg>',
  balancetab: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/></svg>',
  selecto: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>',
  blog: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>',
  chat: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  community: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>',
};

const MODULE_LABEL_KEYS: Record<string, string> = {
  games: 'nav_games',
  memoriz: 'nav_quran',
  balancetab: 'nav_money',
  selecto: 'selecto_title',
  blog: 'blog_title',
  chat: 'chat_title',
  community: 'community_title',
};

const PRAYER_METHODS = [
  { id: 'MuslimWorldLeague', labelKey: 'settings_prayer_method_mwl' },
  { id: 'ISNA', labelKey: 'settings_prayer_method_isna' },
  { id: 'Egypt', labelKey: 'settings_prayer_method_egypt' },
  { id: 'MakkahUmmAlQura', labelKey: 'settings_prayer_method_makkah' },
  { id: 'Karachi', labelKey: 'settings_prayer_method_karachi' },
];

const PRAYER_NAMES = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'] as const;
const REMINDER_OFFSETS = ['off', '5', '10', '15', '30'] as const;

const LANG_OPTIONS: { code: Lang; flag: string; label: string }[] = [
  { code: 'en', flag: '\uD83C\uDDEC\uD83C\uDDE7', label: 'English' },
  { code: 'fr', flag: '\uD83C\uDDEB\uD83C\uDDF7', label: 'Fran\u00e7ais' },
  { code: 'ar', flag: '\uD83C\uDDF8\uD83C\uDDE6', label: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629' },
  { code: 'es', flag: '\uD83C\uDDEA\uD83C\uDDF8', label: 'Espa\u00f1ol' },
  { code: 'de', flag: '\uD83C\uDDE9\uD83C\uDDEA', label: 'Deutsch' },
];

const NOTIFICATION_CATEGORIES = [
  'prayer_reminders',
  'study_reminders',
  'expense_alerts',
  'community_messages',
  'app_updates',
] as const;

const PERMISSIONS_LIST = [
  { key: 'notifications', apiName: 'notifications', iconKey: 'settings_perm_notifications' },
  { key: 'geolocation', apiName: 'geolocation', iconKey: 'settings_perm_location' },
  { key: 'camera', apiName: 'camera', iconKey: 'settings_perm_camera' },
  { key: 'microphone', apiName: 'microphone', iconKey: 'settings_perm_microphone' },
  { key: 'persistent-storage', apiName: 'persistent-storage', iconKey: 'settings_perm_storage' },
] as const;

// ---------- Settings State ----------

interface SettingsState {
  theme: ThemeMode;
  language: Lang;
  enabledModules: Record<string, boolean>;
  prayerMethod: string;
  prayerLocation: { lat: number; lng: number; city: string } | null;
  prayerReminders: Record<string, string>;
  notificationsEnabled: boolean;
  notificationCategories: Record<string, boolean>;
  wifiOnlyDownloads: boolean;
  autoUpdate: boolean;
  travelMode: boolean;
  reduceAnimations: boolean;
}

function getDefaultState(): SettingsState {
  const enabledModules: Record<string, boolean> = {};
  MODULE_IDS.forEach((id) => (enabledModules[id] = true));
  const prayerReminders: Record<string, string> = {};
  PRAYER_NAMES.forEach((p) => (prayerReminders[p] = '15'));
  const notificationCategories: Record<string, boolean> = {};
  NOTIFICATION_CATEGORIES.forEach((c) => (notificationCategories[c] = true));

  return {
    theme: 'system',
    language: 'en',
    enabledModules,
    prayerMethod: 'MuslimWorldLeague',
    prayerLocation: null,
    prayerReminders,
    notificationsEnabled: true,
    notificationCategories,
    wifiOnlyDownloads: true,
    autoUpdate: true,
    travelMode: false,
    reduceAnimations: false,
  };
}

function loadState(): SettingsState {
  const defaults = getDefaultState();
  try {
    const raw = localStorage.getItem(LS_PREFIX + 'state');
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...defaults, ...parsed };
    }
  } catch { /* ignore corrupt data */ }
  return defaults;
}

function saveState(state: SettingsState): void {
  try {
    localStorage.setItem(LS_PREFIX + 'state', JSON.stringify(state));
  } catch { /* quota exceeded — silent */ }
}

// ---------- Helpers ----------

function escHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function toggleClass(el: HTMLElement | null, cls: string, force: boolean): void {
  el?.classList.toggle(cls, force);
}

// ---------- Styles ----------

const SETTINGS_CSS = `
.settings-module { max-width: 640px; margin: 0 auto; padding: 0 16px 120px; }
.settings-module h1 { font-size: 1.5rem; font-weight: 700; margin: 8px 0 20px; color: var(--text-primary); }

/* Accordion */
.s-section { margin-bottom: 8px; border-radius: 16px; overflow: hidden; background: var(--card-bg); box-shadow: var(--shadow-sm); }
.s-header { display: flex; align-items: center; gap: 12px; padding: 16px; cursor: pointer; user-select: none; -webkit-tap-highlight-color: transparent; }
.s-header:hover { background: var(--bg-secondary); }
.s-header-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.s-header-icon svg { width: 20px; height: 20px; }
.s-header-text { flex: 1; }
.s-header-title { font-size: 0.95rem; font-weight: 600; color: var(--text-primary); }
.s-header-subtitle { font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px; }
.s-chevron { width: 20px; height: 20px; color: var(--text-secondary); transition: transform 200ms ease; flex-shrink: 0; }
.s-section.open .s-chevron { transform: rotate(180deg); }
.s-body { max-height: 0; overflow: hidden; transition: max-height 300ms ease; }
.s-section.open .s-body { max-height: 2000px; }
.s-body-inner { padding: 0 16px 16px; }

/* Row */
.s-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); }
.s-row:last-child { border-bottom: none; }
.s-row-label { font-size: 0.875rem; color: var(--text-primary); flex: 1; }
.s-row-sub { font-size: 0.75rem; color: var(--text-secondary); margin-top: 2px; }

/* Toggle Switch */
.s-toggle { position: relative; width: 48px; height: 28px; flex-shrink: 0; }
.s-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.s-toggle-track { position: absolute; inset: 0; border-radius: 14px; background: var(--border); transition: background 200ms ease; cursor: pointer; }
.s-toggle input:checked + .s-toggle-track { background: var(--accent); }
.s-toggle-thumb { position: absolute; top: 2px; left: 2px; width: 24px; height: 24px; border-radius: 50%; background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.2); transition: transform 200ms ease; pointer-events: none; }
.s-toggle input:checked ~ .s-toggle-thumb { transform: translateX(20px); }

/* Select */
.s-select { padding: 8px 12px; border-radius: 10px; border: 1px solid var(--border); background: var(--bg-secondary); color: var(--text-primary); font-size: 0.875rem; min-width: 120px; }
.s-select:focus { outline: 2px solid var(--accent); outline-offset: 1px; }

/* Radio Group */
.s-radio-group { display: flex; gap: 8px; flex-wrap: wrap; }
.s-radio { display: flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 10px; border: 2px solid var(--border); cursor: pointer; font-size: 0.85rem; color: var(--text-primary); transition: all 150ms ease; }
.s-radio:hover { border-color: var(--accent); }
.s-radio.active { border-color: var(--accent); background: rgba(10, 10, 10, 0.1); }
.s-radio input { display: none; }

/* Button */
.s-btn { padding: 10px 20px; border-radius: 12px; border: none; font-size: 0.875rem; font-weight: 600; cursor: pointer; transition: all 150ms ease; }
.s-btn-primary { background: var(--accent); color: var(--accent-text); }
.s-btn-primary:hover { background: var(--accent-hover); }
.s-btn-outline { background: transparent; border: 2px solid var(--border); color: var(--text-primary); }
.s-btn-outline:hover { border-color: var(--accent); color: var(--accent); }
.s-btn-danger { background: transparent; border: 2px solid #EF4444; color: #EF4444; }
.s-btn-danger:hover { background: #EF4444; color: white; }

/* Progress Bar */
.s-progress { width: 100%; height: 8px; border-radius: 4px; background: var(--border); overflow: hidden; }
.s-progress-fill { height: 100%; border-radius: 4px; background: var(--accent); transition: width 300ms ease; }

/* Module Row */
.s-module-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--border); }
.s-module-row:last-child { border-bottom: none; }
.s-module-icon { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.s-module-icon svg { width: 18px; height: 18px; }
.s-module-info { flex: 1; }
.s-module-name { font-size: 0.875rem; font-weight: 600; color: var(--text-primary); }

/* Permission */
.s-perm-status { font-size: 0.75rem; padding: 4px 10px; border-radius: 8px; font-weight: 600; }
.s-perm-granted { background: rgba(10, 10, 10, 0.15); color: #2A2520; }
.s-perm-denied { background: rgba(239, 68, 68, 0.15); color: #EF4444; }
.s-perm-prompt { background: rgba(245, 158, 11, 0.15); color: #D97706; }

/* About */
.s-about-footer { text-align: center; padding: 24px 0 8px; color: var(--text-secondary); font-size: 0.8rem; }
.s-about-footer a { color: var(--accent); text-decoration: none; }

/* Profile Avatar */
.s-avatar { width: 56px; height: 56px; border-radius: 50%; background: var(--accent); color: var(--accent-text); display: flex; align-items: center; justify-content: center; font-size: 1.5rem; font-weight: 700; flex-shrink: 0; }

/* Qibla compass */
.s-qibla { width: 80px; height: 80px; border-radius: 50%; border: 3px solid var(--accent); display: flex; align-items: center; justify-content: center; margin: 8px auto; position: relative; }
.s-qibla-arrow { width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-bottom: 32px solid var(--accent); transform-origin: center bottom; position: absolute; bottom: 50%; }

[dir="rtl"] .s-chevron { transform: rotate(0deg); }
[dir="rtl"] .s-section.open .s-chevron { transform: rotate(180deg); }
[dir="rtl"] .s-toggle-thumb { left: auto; right: 2px; }
[dir="rtl"] .s-toggle input:checked ~ .s-toggle-thumb { transform: translateX(-20px); }
`;

// ---------- SVG Icons ----------

const CHEVRON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';

const SECTION_ICONS: Record<string, { svg: string; bg: string; fg: string }> = {
  profile: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="7" r="4"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></svg>',
    bg: '#3B82F620', fg: '#3B82F6',
  },
  appearance: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>',
    bg: '#F59E0B20', fg: '#F59E0B',
  },
  modules: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>',
    bg: '#8B5CF620', fg: '#8B5CF6',
  },
  prayer: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"/></svg>',
    bg: '#0A0A0A20', fg: '#0A0A0A',
  },
  notifications: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>',
    bg: '#EF444420', fg: '#EF4444',
  },
  storage: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>',
    bg: '#14B8A620', fg: '#14B8A6',
  },
  battery: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="6" width="18" height="12" rx="2"/><line x1="23" y1="13" x2="23" y2="11"/><line x1="5" y1="10" x2="5" y2="14"/><line x1="9" y1="10" x2="9" y2="14"/><line x1="13" y1="10" x2="13" y2="14"/></svg>',
    bg: '#F9731620', fg: '#F97316',
  },
  permissions: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    bg: '#6366F120', fg: '#6366F1',
  },
  about: {
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
    bg: '#6B728020', fg: '#6B7280',
  },
};

// ---------- Module Colors ----------

const MODULE_COLORS: Record<string, string> = {
  games: '#3B82F6',
  memoriz: '#F59E0B',
  balancetab: '#8B5CF6',
  selecto: '#F97316',
  blog: '#14B8A6',
  chat: '#0A0A0A',
  community: '#6366F1',
};

// ---------- SettingsModule Class ----------

export default class SettingsModule implements AweniModule {
  private container: HTMLElement | null = null;
  private context: AweniContext | null = null;
  private state: SettingsState = getDefaultState();
  private styleEl: HTMLStyleElement | null = null;

  async mount(container: HTMLElement, context: AweniContext): Promise<void> {
    this.container = container;
    this.context = context;
    this.state = loadState();

    // Apply current language from context
    this.state.language = context.i18n.locale;
    this.state.theme = context.theme.mode;

    // Inject scoped CSS
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = SETTINGS_CSS;
    document.head.appendChild(this.styleEl);

    this.render();
    this.bindEvents();
  }

  unmount(): void {
    if (this.styleEl) {
      this.styleEl.remove();
      this.styleEl = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.context = null;
  }

  // ---------- Render ----------

  private render(): void {
    if (!this.container || !this.context) return;
    const t = this.context.i18n.t.bind(this.context.i18n);
    const isRtl = this.context.i18n.dir === 'rtl';
    const s = this.state;
    const user = this.context.auth.user;
    const initials = (user?.name || 'U').split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
    const activeModCount = Object.values(s.enabledModules).filter(Boolean).length;
    const isQuranActive = s.enabledModules.memoriz;

    this.container.innerHTML = `
      <div class="settings-module" dir="${isRtl ? 'rtl' : 'ltr'}">
        <h1>${escHtml(t('settings_title'))}</h1>

        <!-- A. Profile & Account -->
        ${this.renderSection('profile', t('settings_profile'), t('settings_profile_sub'), `
          <div style="display:flex;align-items:center;gap:16px;margin-bottom:16px;">
            <div class="s-avatar">${user?.avatar ? `<img src="${escHtml(user.avatar)}" style="width:100%;height:100%;border-radius:50%;object-fit:cover;" alt="">` : initials}</div>
            <div>
              <div style="font-weight:700;font-size:1rem;color:var(--text-primary)">${escHtml(user?.name || 'Guest')}</div>
              <div style="font-size:0.8rem;color:var(--text-secondary)">${escHtml(user?.email || '')}</div>
              <div style="font-size:0.75rem;color:var(--accent);margin-top:4px;">${escHtml(t('settings_active_profile'))}: ${escHtml(user?.name || 'Self')}</div>
            </div>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button class="s-btn s-btn-outline" data-action="manage-profiles">${escHtml(t('settings_manage_profiles'))}</button>
            <a href="/identity/" class="s-btn s-btn-outline" style="text-decoration:none;display:inline-flex;align-items:center;">${escHtml(t('settings_full_identity'))}</a>
          </div>
        `)}

        <!-- B. Appearance -->
        ${this.renderSection('appearance', t('settings_appearance'), t('settings_appearance_sub'), `
          <div class="s-row" style="flex-direction:column;align-items:stretch;">
            <div class="s-row-label" style="margin-bottom:8px;font-weight:600;">${escHtml(t('settings_theme'))}</div>
            <div class="s-radio-group">
              ${(['light', 'dark', 'system'] as const).map((m) => `
                <label class="s-radio ${s.theme === m ? 'active' : ''}" data-theme="${m}">
                  <input type="radio" name="theme" value="${m}" ${s.theme === m ? 'checked' : ''}>
                  ${escHtml(t(`settings_theme_${m}`))}
                </label>
              `).join('')}
            </div>
          </div>
          <div class="s-row">
            <div class="s-row-label">
              <div>${escHtml(t('settings_language'))}</div>
            </div>
            <select class="s-select" data-action="change-lang">
              ${LANG_OPTIONS.map((l) => `
                <option value="${l.code}" ${s.language === l.code ? 'selected' : ''}>${l.flag} ${l.label}</option>
              `).join('')}
            </select>
          </div>
        `)}

        <!-- C. Modules -->
        ${this.renderSection('modules', t('settings_modules'), `${activeModCount} ${t('settings_active_modules')}`, `
          ${MODULE_IDS.map((id) => `
            <div class="s-module-row">
              <div class="s-module-icon" style="background:${MODULE_COLORS[id]}20;color:${MODULE_COLORS[id]}">
                ${MODULE_ICONS[id]}
              </div>
              <div class="s-module-info">
                <div class="s-module-name">${escHtml(t(MODULE_LABEL_KEYS[id]))}</div>
              </div>
              <label class="s-toggle">
                <input type="checkbox" data-module-toggle="${id}" ${s.enabledModules[id] ? 'checked' : ''}>
                <span class="s-toggle-track"></span>
                <span class="s-toggle-thumb"></span>
              </label>
            </div>
          `).join('')}
        `)}

        <!-- D. Prayer Settings -->
        ${isQuranActive ? this.renderSection('prayer', t('settings_prayer'), t('settings_prayer_sub'), `
          <div class="s-row">
            <div class="s-row-label">${escHtml(t('settings_prayer_calc_method'))}</div>
            <select class="s-select" data-action="change-prayer-method">
              ${PRAYER_METHODS.map((m) => `
                <option value="${m.id}" ${s.prayerMethod === m.id ? 'selected' : ''}>${escHtml(t(m.labelKey))}</option>
              `).join('')}
            </select>
          </div>
          <div class="s-row" style="flex-direction:column;align-items:stretch;">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
              <div class="s-row-label" style="font-weight:600;">${escHtml(t('settings_location'))}</div>
              <button class="s-btn s-btn-primary" data-action="use-gps" style="padding:6px 14px;font-size:0.8rem;">${escHtml(t('settings_use_gps'))}</button>
            </div>
            <div style="font-size:0.8rem;color:var(--text-secondary);">
              ${s.prayerLocation ? escHtml(s.prayerLocation.city || `${s.prayerLocation.lat.toFixed(4)}, ${s.prayerLocation.lng.toFixed(4)}`) : escHtml(t('settings_no_location'))}
            </div>
            <input type="text" class="s-select" style="margin-top:8px;width:100%;" placeholder="${escHtml(t('settings_city_input'))}" data-action="city-input" value="${escHtml(s.prayerLocation?.city || '')}">
          </div>
          <div style="margin-top:8px;">
            <div class="s-row-label" style="font-weight:600;margin-bottom:8px;">${escHtml(t('settings_reminders'))}</div>
            ${PRAYER_NAMES.map((p) => `
              <div class="s-row">
                <div class="s-row-label">${p}</div>
                <select class="s-select" data-prayer-reminder="${p}" style="min-width:80px;">
                  ${REMINDER_OFFSETS.map((o) => `
                    <option value="${o}" ${s.prayerReminders[p] === o ? 'selected' : ''}>${o === 'off' ? escHtml(t('settings_off')) : `${o} ${escHtml(t('settings_min'))}`}</option>
                  `).join('')}
                </select>
              </div>
            `).join('')}
          </div>
          <div style="margin-top:12px;">
            <div class="s-row-label" style="font-weight:600;margin-bottom:8px;">${escHtml(t('settings_qibla'))}</div>
            <div class="s-qibla">
              <div class="s-qibla-arrow" style="transform:rotate(${this.getQiblaDirection()}deg);"></div>
            </div>
          </div>
        `) : ''}

        <!-- E. Notifications -->
        ${this.renderSection('notifications', t('settings_notifications'), '', `
          <div class="s-row">
            <div class="s-row-label" style="font-weight:600;">${escHtml(t('settings_enable_all'))}</div>
            <label class="s-toggle">
              <input type="checkbox" data-action="toggle-all-notifs" ${s.notificationsEnabled ? 'checked' : ''}>
              <span class="s-toggle-track"></span>
              <span class="s-toggle-thumb"></span>
            </label>
          </div>
          ${NOTIFICATION_CATEGORIES.map((c) => `
            <div class="s-row" ${!s.notificationsEnabled ? 'style="opacity:0.4;pointer-events:none;"' : ''}>
              <div class="s-row-label">${escHtml(t(`settings_notif_${c}`))}</div>
              <label class="s-toggle">
                <input type="checkbox" data-notif-toggle="${c}" ${s.notificationCategories[c] ? 'checked' : ''}>
                <span class="s-toggle-track"></span>
                <span class="s-toggle-thumb"></span>
              </label>
            </div>
          `).join('')}
        `)}

        <!-- F. Offline & Storage -->
        ${this.renderSection('storage', t('settings_storage'), '', `
          <div style="margin-bottom:16px;">
            <div style="display:flex;justify-content:space-between;margin-bottom:6px;">
              <span class="s-row-label" style="font-weight:600;">${escHtml(t('settings_storage_used'))}</span>
              <span style="font-size:0.8rem;color:var(--text-secondary);" id="storage-usage">--</span>
            </div>
            <div class="s-progress"><div class="s-progress-fill" id="storage-bar" style="width:0%"></div></div>
          </div>
          <div id="storage-breakdown" style="margin-bottom:16px;font-size:0.8rem;color:var(--text-secondary);"></div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:16px;">
            <button class="s-btn s-btn-primary" data-action="download-quran">${escHtml(t('settings_download_quran'))}</button>
            <button class="s-btn s-btn-outline" data-action="download-games">${escHtml(t('settings_download_games'))}</button>
            <button class="s-btn s-btn-outline" data-action="download-blog">${escHtml(t('settings_download_blog'))}</button>
          </div>
          <div id="download-progress" style="display:none;margin-bottom:16px;">
            <div class="s-progress"><div class="s-progress-fill" id="download-bar" style="width:0%"></div></div>
            <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;" id="download-label"></div>
          </div>
          <button class="s-btn s-btn-danger" data-action="clear-cache" style="width:100%;margin-bottom:12px;">${escHtml(t('settings_clear_cache'))}</button>
          <div class="s-row">
            <div class="s-row-label">${escHtml(t('settings_auto_update'))}</div>
            <label class="s-toggle">
              <input type="checkbox" data-action="toggle-auto-update" ${s.autoUpdate ? 'checked' : ''}>
              <span class="s-toggle-track"></span>
              <span class="s-toggle-thumb"></span>
            </label>
          </div>
          <div class="s-row">
            <div class="s-row-label">
              <div>${escHtml(t('settings_wifi_only'))}</div>
              <div class="s-row-sub">${escHtml(t('settings_wifi_only_sub'))}</div>
            </div>
            <label class="s-toggle">
              <input type="checkbox" data-action="toggle-wifi-only" ${s.wifiOnlyDownloads ? 'checked' : ''}>
              <span class="s-toggle-track"></span>
              <span class="s-toggle-thumb"></span>
            </label>
          </div>
        `)}

        <!-- G. Battery & Performance -->
        ${this.renderSection('battery', t('settings_battery'), '', `
          <div class="s-row">
            <div class="s-row-label">
              <div>${escHtml(t('settings_travel_mode'))}</div>
              <div class="s-row-sub">${escHtml(t('settings_travel_mode_sub'))}</div>
            </div>
            <label class="s-toggle">
              <input type="checkbox" data-action="toggle-travel" ${s.travelMode ? 'checked' : ''}>
              <span class="s-toggle-track"></span>
              <span class="s-toggle-thumb"></span>
            </label>
          </div>
          <div class="s-row">
            <div class="s-row-label">${escHtml(t('settings_performance'))}</div>
            <span style="font-size:0.85rem;font-weight:600;color:var(--accent);" id="perf-level">${escHtml(t('settings_perf_high'))}</span>
          </div>
          <div class="s-row">
            <div class="s-row-label">${escHtml(t('settings_reduce_animations'))}</div>
            <label class="s-toggle">
              <input type="checkbox" data-action="toggle-animations" ${s.reduceAnimations ? 'checked' : ''}>
              <span class="s-toggle-track"></span>
              <span class="s-toggle-thumb"></span>
            </label>
          </div>
        `)}

        <!-- H. Permissions -->
        ${this.renderSection('permissions', t('settings_permissions'), '', `
          <div id="perm-list"></div>
        `)}

        <!-- I. About -->
        ${this.renderSection('about', t('settings_about'), 'v0.1.0', `
          <div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:8px 0 16px;">
            <div style="width:72px;height:72px;border-radius:18px;background:#FAF7F2;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(10,10,10,0.08);">
              <img src="/aweni/icons/pwa-192x192.png" alt="AWENI" width="56" height="56" style="display:block;" />
            </div>
            <div style="font-size:0.75rem;color:var(--text-secondary);letter-spacing:1.5px;text-transform:uppercase;">AWENI&trade;</div>
          </div>
          <div class="s-row">
            <div class="s-row-label">${escHtml(t('settings_version'))}</div>
            <span style="font-size:0.85rem;color:var(--text-secondary);">0.1.0</span>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
            <button class="s-btn s-btn-outline" data-action="check-update">${escHtml(t('settings_check_updates'))}</button>
            <button class="s-btn s-btn-outline" data-action="report-bug">${escHtml(t('settings_report_bug'))}</button>
          </div>
          <div style="margin-top:16px;">
            <a href="#" data-action="terms" style="display:block;padding:8px 0;font-size:0.875rem;color:var(--accent);text-decoration:none;">${escHtml(t('settings_terms'))}</a>
            <a href="#" data-action="privacy" style="display:block;padding:8px 0;font-size:0.875rem;color:var(--accent);text-decoration:none;">${escHtml(t('settings_privacy'))}</a>
          </div>
          <div class="s-about-footer">
            ${escHtml(t('settings_made_with_love'))}
          </div>
        `)}
      </div>
    `;

    // Post-render: populate permissions & storage
    this.populatePermissions();
    this.populateStorage();
    this.detectPerformance();
  }

  private renderSection(key: string, title: string, subtitle: string, body: string): string {
    const icon = SECTION_ICONS[key];
    return `
      <div class="s-section" data-section="${key}">
        <div class="s-header" role="button" tabindex="0" aria-expanded="false" data-toggle-section="${key}">
          <div class="s-header-icon" style="background:${icon.bg};color:${icon.fg}">${icon.svg}</div>
          <div class="s-header-text">
            <div class="s-header-title">${escHtml(title)}</div>
            ${subtitle ? `<div class="s-header-subtitle">${escHtml(subtitle)}</div>` : ''}
          </div>
          <div class="s-chevron">${CHEVRON_SVG}</div>
        </div>
        <div class="s-body"><div class="s-body-inner">${body}</div></div>
      </div>
    `;
  }

  // ---------- Events ----------

  private bindEvents(): void {
    if (!this.container || !this.context) return;
    const el = this.container;
    const ctx = this.context;

    // Accordion toggle
    el.querySelectorAll<HTMLElement>('[data-toggle-section]').forEach((header) => {
      header.addEventListener('click', () => {
        const section = header.closest('.s-section');
        if (section) {
          const isOpen = section.classList.contains('open');
          section.classList.toggle('open', !isOpen);
          header.setAttribute('aria-expanded', String(!isOpen));
        }
      });
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); header.click(); }
      });
    });

    // Theme radio
    el.querySelectorAll<HTMLLabelElement>('[data-theme]').forEach((label) => {
      label.addEventListener('click', () => {
        const value = label.dataset.theme as ThemeMode;
        this.state.theme = value;
        this.save();
        // Update theme service
        if (ctx.theme.mode !== value) {
          ctx.theme.toggle(); // cycle theme
          // Re-render to update active states
          this.render();
          this.bindEvents();
        }
      });
    });

    // Language select
    const langSelect = el.querySelector<HTMLSelectElement>('[data-action="change-lang"]');
    langSelect?.addEventListener('change', () => {
      const lang = langSelect.value as Lang;
      this.state.language = lang;
      this.save();
      // Trigger full app language change
      window.dispatchEvent(new CustomEvent('aweni:lang-change', { detail: { lang } }));
      // Re-render with new translations
      setTimeout(() => { this.render(); this.bindEvents(); }, 100);
    });

    // Module toggles
    el.querySelectorAll<HTMLInputElement>('[data-module-toggle]').forEach((input) => {
      input.addEventListener('change', () => {
        const id = input.dataset.moduleToggle!;
        this.state.enabledModules[id] = input.checked;
        this.save();
        window.dispatchEvent(new CustomEvent('aweni:modules-changed', { detail: { enabledModules: this.state.enabledModules } }));
        // Update subtitle count
        const section = el.querySelector('[data-section="modules"]');
        const sub = section?.querySelector('.s-header-subtitle');
        if (sub) {
          const count = Object.values(this.state.enabledModules).filter(Boolean).length;
          sub.textContent = `${count} ${this.context!.i18n.t('settings_active_modules')}`;
        }
        // Show/hide prayer section
        if (id === 'memoriz') {
          this.render();
          this.bindEvents();
        }
      });
    });

    // Prayer method
    const prayerMethodSelect = el.querySelector<HTMLSelectElement>('[data-action="change-prayer-method"]');
    prayerMethodSelect?.addEventListener('change', () => {
      this.state.prayerMethod = prayerMethodSelect.value;
      this.save();
    });

    // GPS button
    el.querySelector<HTMLButtonElement>('[data-action="use-gps"]')?.addEventListener('click', () => {
      this.requestGeolocation();
    });

    // Prayer reminders
    el.querySelectorAll<HTMLSelectElement>('[data-prayer-reminder]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const prayer = sel.dataset.prayerReminder!;
        this.state.prayerReminders[prayer] = sel.value;
        this.save();
      });
    });

    // Notifications master toggle
    el.querySelector<HTMLInputElement>('[data-action="toggle-all-notifs"]')?.addEventListener('change', (e) => {
      this.state.notificationsEnabled = (e.target as HTMLInputElement).checked;
      this.save();
      this.render();
      this.bindEvents();
    });

    // Notification category toggles
    el.querySelectorAll<HTMLInputElement>('[data-notif-toggle]').forEach((input) => {
      input.addEventListener('change', () => {
        const cat = input.dataset.notifToggle!;
        this.state.notificationCategories[cat] = input.checked;
        this.save();
      });
    });

    // Storage toggles
    el.querySelector<HTMLInputElement>('[data-action="toggle-auto-update"]')?.addEventListener('change', (e) => {
      this.state.autoUpdate = (e.target as HTMLInputElement).checked;
      this.save();
    });
    el.querySelector<HTMLInputElement>('[data-action="toggle-wifi-only"]')?.addEventListener('change', (e) => {
      this.state.wifiOnlyDownloads = (e.target as HTMLInputElement).checked;
      this.save();
    });

    // Download buttons
    el.querySelector('[data-action="download-quran"]')?.addEventListener('click', () => this.simulateDownload('Quran'));
    el.querySelector('[data-action="download-games"]')?.addEventListener('click', () => this.simulateDownload('Games'));
    el.querySelector('[data-action="download-blog"]')?.addEventListener('click', () => this.simulateDownload('Blog'));

    // Clear cache
    el.querySelector('[data-action="clear-cache"]')?.addEventListener('click', () => {
      if (confirm(this.context!.i18n.t('settings_clear_cache_confirm'))) {
        caches?.keys().then((names) => names.forEach((n) => caches.delete(n)));
        this.context!.showNotification(this.context!.i18n.t('settings_cache_cleared'), 'success');
        this.populateStorage();
      }
    });

    // Battery toggles
    el.querySelector<HTMLInputElement>('[data-action="toggle-travel"]')?.addEventListener('change', (e) => {
      this.state.travelMode = (e.target as HTMLInputElement).checked;
      this.save();
    });
    el.querySelector<HTMLInputElement>('[data-action="toggle-animations"]')?.addEventListener('change', (e) => {
      this.state.reduceAnimations = (e.target as HTMLInputElement).checked;
      this.save();
      document.documentElement.classList.toggle('reduce-motion', this.state.reduceAnimations);
    });

    // About actions
    el.querySelector('[data-action="check-update"]')?.addEventListener('click', () => {
      ctx.showNotification(ctx.i18n.t('settings_up_to_date'), 'info');
    });
    el.querySelector('[data-action="report-bug"]')?.addEventListener('click', () => {
      window.open('mailto:support@learnature-ly.com?subject=AWENI Bug Report', '_blank');
    });
  }

  // ---------- Helpers ----------

  private save(): void {
    saveState(this.state);
    // Fire-and-forget server sync
    this.context?.api.put('/aweni/api/v1/preferences', this.state).catch(() => { /* silent */ });
  }

  private getQiblaDirection(): number {
    if (!this.state.prayerLocation) return 135; // default SE
    const lat = this.state.prayerLocation.lat * Math.PI / 180;
    const lng = this.state.prayerLocation.lng * Math.PI / 180;
    const kaabaLat = 21.4225 * Math.PI / 180;
    const kaabaLng = 39.8262 * Math.PI / 180;
    const y = Math.sin(kaabaLng - lng);
    const x = Math.cos(lat) * Math.tan(kaabaLat) - Math.sin(lat) * Math.cos(kaabaLng - lng);
    return Math.round(Math.atan2(y, x) * 180 / Math.PI);
  }

  private requestGeolocation(): void {
    if (!navigator.geolocation) {
      this.context?.showNotification(this.context.i18n.t('settings_geo_unavailable'), 'error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.state.prayerLocation = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          city: `${pos.coords.latitude.toFixed(2)}, ${pos.coords.longitude.toFixed(2)}`,
        };
        this.save();
        this.render();
        this.bindEvents();
        this.context?.showNotification(this.context.i18n.t('settings_location_updated'), 'success');
      },
      () => {
        this.context?.showNotification(this.context.i18n.t('settings_geo_denied'), 'warning');
      },
    );
  }

  private async populatePermissions(): Promise<void> {
    const list = this.container?.querySelector('#perm-list');
    if (!list || !this.context) return;
    const t = this.context.i18n.t.bind(this.context.i18n);
    let html = '';

    for (const perm of PERMISSIONS_LIST) {
      let status: 'granted' | 'denied' | 'prompt' = 'prompt';
      try {
        if ('permissions' in navigator) {
          const result = await navigator.permissions.query({ name: perm.apiName as PermissionName });
          status = result.state as 'granted' | 'denied' | 'prompt';
        }
      } catch { /* unsupported permission query */ }

      const statusLabel = t(`settings_perm_${status}`);
      const statusClass = `s-perm-${status}`;

      html += `
        <div class="s-row">
          <div class="s-row-label">
            <div>${escHtml(t(perm.iconKey))}</div>
            <div class="s-row-sub">${escHtml(t(`${perm.iconKey}_why`))}</div>
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            <span class="s-perm-status ${statusClass}">${escHtml(statusLabel)}</span>
            ${status === 'prompt' ? `<button class="s-btn s-btn-primary" style="padding:4px 12px;font-size:0.75rem;" data-grant-perm="${perm.key}">${escHtml(t('settings_grant'))}</button>` : ''}
          </div>
        </div>
      `;
    }
    list.innerHTML = html;

    // Bind grant buttons
    list.querySelectorAll<HTMLButtonElement>('[data-grant-perm]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.grantPerm;
        if (key === 'notifications') Notification.requestPermission();
        else if (key === 'geolocation') navigator.geolocation.getCurrentPosition(() => {}, () => {});
        else if (key === 'camera') navigator.mediaDevices?.getUserMedia({ video: true }).then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {});
        else if (key === 'microphone') navigator.mediaDevices?.getUserMedia({ audio: true }).then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {});
        else if (key === 'persistent-storage') navigator.storage?.persist();
        // Refresh after a moment
        setTimeout(() => { this.populatePermissions(); }, 1000);
      });
    });
  }

  private async populateStorage(): Promise<void> {
    const usageEl = this.container?.querySelector('#storage-usage');
    const barEl = this.container?.querySelector<HTMLElement>('#storage-bar');
    const breakdownEl = this.container?.querySelector('#storage-breakdown');
    if (!usageEl || !barEl || !breakdownEl) return;
    const t = this.context!.i18n.t.bind(this.context!.i18n);

    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const est = await navigator.storage.estimate();
        const usedMB = ((est.usage || 0) / (1024 * 1024)).toFixed(1);
        const quotaMB = ((est.quota || 0) / (1024 * 1024)).toFixed(0);
        const pct = est.quota ? Math.min(100, ((est.usage || 0) / est.quota) * 100).toFixed(1) : '0';
        usageEl.textContent = `${usedMB} MB / ${quotaMB} MB`;
        barEl.style.width = `${pct}%`;

        // Breakdown estimate (proportional mock since we cannot measure per-cache)
        const totalUsed = est.usage || 1;
        breakdownEl.innerHTML = `
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;">
            <span>${t('nav_games')}: ~${((totalUsed * 0.4) / (1024 * 1024)).toFixed(1)} MB</span>
            <span>${t('nav_quran')}: ~${((totalUsed * 0.3) / (1024 * 1024)).toFixed(1)} MB</span>
            <span>${t('blog_title')}: ~${((totalUsed * 0.2) / (1024 * 1024)).toFixed(1)} MB</span>
            <span>${t('settings_other')}: ~${((totalUsed * 0.1) / (1024 * 1024)).toFixed(1)} MB</span>
          </div>
        `;
      } else {
        usageEl.textContent = t('settings_storage_unavailable');
      }
    } catch {
      usageEl.textContent = t('settings_storage_unavailable');
    }
  }

  private detectPerformance(): void {
    const el = this.container?.querySelector('#perf-level');
    if (!el || !this.context) return;
    const t = this.context.i18n.t.bind(this.context.i18n);
    const cores = navigator.hardwareConcurrency || 2;
    const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory || 4;
    if (cores >= 4 && mem >= 4) el.textContent = t('settings_perf_high');
    else if (cores >= 2 && mem >= 2) el.textContent = t('settings_perf_medium');
    else el.textContent = t('settings_perf_low');
  }

  private simulateDownload(pack: string): void {
    const progressEl = this.container?.querySelector<HTMLElement>('#download-progress');
    const barEl = this.container?.querySelector<HTMLElement>('#download-bar');
    const labelEl = this.container?.querySelector<HTMLElement>('#download-label');
    if (!progressEl || !barEl || !labelEl) return;
    const t = this.context!.i18n.t.bind(this.context!.i18n);

    progressEl.style.display = 'block';
    labelEl.textContent = `${t('settings_downloading')} ${pack}...`;
    let pct = 0;
    const interval = setInterval(() => {
      pct += Math.random() * 15;
      if (pct >= 100) {
        pct = 100;
        clearInterval(interval);
        labelEl.textContent = t('settings_download_complete');
        setTimeout(() => { progressEl.style.display = 'none'; }, 2000);
        this.context?.showNotification(`${pack} ${t('settings_download_complete').toLowerCase()}`, 'success');
      }
      barEl.style.width = `${Math.min(100, pct)}%`;
    }, 200);
  }
}
